import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Chapter, Reveal } from "@/components/Chapter";
import { useContent } from "@/content";

const initials = (n) => n.split(" ").map((w) => w[0]).slice(0, 2).join("");

export const Testimonials = () => {
  const { TESTIMONIALS } = useContent();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const n = TESTIMONIALS.length;

  const go = (d) => {
    setDir(d);
    setI((p) => (p + d + n) % n);
  };

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => go(1), 6500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, i]);

  const t = TESTIMONIALS[i];

  return (
    <section id="testimonials" className="relative max-w-7xl mx-auto px-4 sm:px-8 py-24 md:py-32" data-testid="testimonials-section">
      <div className="glow-amber-light absolute -top-10 -left-40 h-[480px] w-[480px] rounded-full pointer-events-none" />
      <Chapter id="03" name="Client voices" title="What clients say when the work is done." sub="Every quote here comes from a project we delivered — on time, with a team the client could always reach." />

      <div className="relative grid lg:grid-cols-[auto_1fr] gap-8 lg:gap-14 items-start" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <Reveal>
          <motion.span
            animate={{ y: [0, -8, 0], rotate: [0, -4, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="hidden lg:flex h-24 w-24 items-center justify-center rounded-3xl bg-[#0B1528] shadow-[0_24px_50px_-20px_rgba(11,21,40,0.5)]"
          >
            <Quote className="h-10 w-10 text-amber-400" />
          </motion.span>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative rounded-3xl border border-slate-200 bg-white p-8 md:p-12 shadow-[0_30px_70px_-40px_rgba(11,21,40,0.35)] overflow-hidden" data-testid="testimonial-card">
            <span className="absolute -top-6 right-8 font-extrabold text-[9rem] leading-none text-amber-400/10 select-none">"</span>
            <div className="flex gap-1 mb-6" aria-label="5 star rating">
              {[0, 1, 2, 3, 4].map((s) => (
                <motion.span key={s} initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 + s * 0.07, type: "spring", stiffness: 300 }}>
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                </motion.span>
              ))}
            </div>
            <div className="relative min-h-[150px] sm:min-h-[120px]">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.blockquote
                  key={i}
                  custom={dir}
                  initial={{ opacity: 0, x: 40 * dir }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 * dir }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="testimonial-quote"
                >
                  <p className="text-lg sm:text-xl lg:text-2xl font-medium leading-relaxed text-[#0B1528]">{t.quote}</p>
                  <footer className="mt-8 flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-amber-500 font-bold text-[#0B1528]">{initials(t.name)}</span>
                    <span>
                      <span className="block text-sm font-semibold text-[#0B1528]" data-testid="testimonial-name">{t.name}</span>
                      <span className="block text-xs text-slate-500">{t.role} · {t.company}</span>
                    </span>
                    <span className="ml-auto hidden sm:inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500">{t.service}</span>
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
            </div>

            <div className="mt-10 flex items-center justify-between">
              <div className="flex gap-2" data-testid="testimonial-dots">
                {TESTIMONIALS.map((_, d) => (
                  <button
                    key={d}
                    onClick={() => { setDir(d > i ? 1 : -1); setI(d); }}
                    aria-label={`Show testimonial ${d + 1}`}
                    data-testid={`testimonial-dot-${d}`}
                    className={`h-2 rounded-full transition-all duration-500 ${d === i ? "w-8 bg-amber-500" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => go(-1)} aria-label="Previous testimonial" data-testid="testimonial-prev" className="rounded-full border border-slate-300 p-2.5 text-slate-600 hover:border-amber-500 hover:text-amber-600 active:scale-90 transition-all">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => go(1)} aria-label="Next testimonial" data-testid="testimonial-next" className="rounded-full bg-[#0B1528] p-2.5 text-white hover:bg-amber-500 hover:text-[#0B1528] active:scale-90 transition-all">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
